import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const LS_EDITOR = "tierpt_editor_mode";

const Ctx = createContext({
  content: {},
  save: async () => {},
  reset: async () => {},
  loaded: false,
  isAdmin: false,
  editorMode: false,
  setEditorMode: () => {},
});

export const useSiteContent = () => useContext(Ctx);

export function SiteContentProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [content, setContent] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [editorPref, setEditorPref] = useState(() => {
    try {
      return localStorage.getItem(LS_EDITOR) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    api
      .get("/site-content")
      .then((r) => setContent(r.data || {}))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const setEditorMode = useCallback((on) => {
    setEditorPref(on);
    try {
      localStorage.setItem(LS_EDITOR, on ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  const save = useCallback(async (key, value) => {
    await api.put("/admin/site-content", { key, value });
    setContent((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(async (key) => {
    await api.delete("/admin/site-content", { params: { key } });
    setContent((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      content,
      save,
      reset,
      loaded,
      isAdmin,
      editorMode: isAdmin && editorPref,
      setEditorMode,
    }),
    [content, save, reset, loaded, isAdmin, editorPref, setEditorMode]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Admin-only switch (renders nothing for everyone else). */
export function EditorModeToggle({ className = "" }) {
  const { isAdmin, editorMode, setEditorMode } = useSiteContent();
  if (!isAdmin) return null;
  return (
    <button
      type="button"
      onClick={() => setEditorMode(!editorMode)}
      aria-pressed={editorMode}
      title={editorMode ? "Editor mode on — click to turn off" : "Turn on editor mode"}
      data-testid="editor-mode-toggle"
      className={`inline-flex items-center gap-2 px-2.5 py-1 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors ${
        editorMode ? "text-amber-300" : "text-stone-400 hover:text-amber-200"
      } ${className}`}
    >
      <span
        className={`relative inline-block w-7 h-3.5 rounded-full transition-colors ${
          editorMode ? "bg-amber-400" : "bg-stone-700"
        }`}
      >
        <span
          className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-[#0A0A0C] transition-all ${
            editorMode ? "left-[16px]" : "left-0.5"
          }`}
        />
      </span>
      Editor
    </button>
  );
}

/** Small fixed reminder while editor mode is on. Click to turn it off. */
export function EditorModePill() {
  const { editorMode, setEditorMode } = useSiteContent();
  if (!editorMode) return null;
  return (
    <button
      type="button"
      onClick={() => setEditorMode(false)}
      title="Click to turn editor mode off"
      data-testid="editor-mode-pill"
      className="btn-gold fixed bottom-4 left-4 z-[60] text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-2 rounded-sm shadow-lg"
    >
      ● Editor mode on
    </button>
  );
}
